import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import HomeView from '@/views/HomeView.vue'
import ScanView from '@/views/ScanView.vue'

/**
 * Four routes, and the URL carries everything worth returning to.
 *
 * `/scan/:id` is the whole sharing story. The schema makes the scan id an
 * unguessable token precisely so that the result page is a link somebody can
 * bookmark, reload, and send to whoever actually runs the server. Nothing about
 * a report is held anywhere the back button cannot reach: the category sections
 * are addressable by fragment, and whether the passing checks are expanded is a
 * query parameter rather than component state, so "look at the TLS section with
 * the passes open" is a link.
 *
 * `HomeView` and `ScanView` are bundled with the entry chunk. Splitting them
 * would put a network round trip between pressing the button and seeing the
 * page, which is the one moment in this product where latency is felt. The two
 * pages nobody is waiting on are split out.
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: { title: 'Check a website’s security configuration' },
  },
  {
    path: '/scan/:id',
    name: 'scan',
    component: ScanView,
    props: true,
    meta: { title: 'Scan report' },
  },
  {
    path: '/checks',
    name: 'checks',
    component: () => import('@/views/ChecksView.vue'),
    meta: { title: 'What we test' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: 'Page not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,

  /**
   * Back restores where you were. A fragment goes to its section. A new page
   * starts at the top.
   *
   * No `top` offset here, and no `behavior`. The sticky header is cleared by
   * `scroll-mt-24` on the sections themselves, so putting a second offset in
   * this function would double it, and `behavior` is left to the
   * `scroll-behavior` rule in main.css, which is already switched off under
   * prefers-reduced-motion.
   *
   * A fragment pointing at a section that has not rendered yet is not handled
   * here. On a cold load of `/scan/<id>#category-tls` the report is still a
   * skeleton at this point, so `ScanView` holds the fragment and applies it
   * when the section appears.
   */
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash }
    return { top: 0 }
  },
})

const SITE_NAME = 'healthcheck'

router.afterEach((to) => {
  const title = typeof to.meta.title === 'string' ? to.meta.title : ''
  document.title = title ? `${title} — ${SITE_NAME}` : SITE_NAME
})
